import * as React from 'react';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import {
  Box,
  ElevateLogoIcon,
  Image,
  NavigationLeftIcon,
  RawButton,
  Text,
  Tip,
  useResponsive,
} from '@common-fe/common-fe';

import { SwitchImage } from '@/components/elements';
import { useBreadcrumbsStore } from '@/modules/core/store';
import { useOrganizationProperties } from '@/modules/transaction/hooks';
import { whiteLabelingConfig } from '@/utils/whiteLabeling';

import { BackButtonWrap, Breadcrumb, Content, Delimiter } from './Breadcrumbs.styles';

const ICON_SIZE = 16;

export const Breadcrumbs: React.FC = () => {
  const { breadcrumbs, backBtn, backBtnTitle } = useBreadcrumbsStore();

  const history = useHistory();
  const { t } = useTranslation();
  const { organizationProperties } = useOrganizationProperties();
  const faviconUrl = organizationProperties?.faviconUrl;
  const minimizedLogoUrl = organizationProperties?.minimizedLogoUrl;

  const lastIndex = useMemo(() => breadcrumbs.length - 1, [breadcrumbs]);
  const handleRedirect = useCallback(
    (url: string) => {
      history.push(url);
    },
    [history]
  );
  const backButtonHandler = useCallback(() => {
    if (breadcrumbs[lastIndex - 1]) {
      history.push(breadcrumbs[lastIndex - 1].value);
    }
  }, [history, breadcrumbs, lastIndex]);
  const { isMobile } = useResponsive();

  if (isMobile) {
    const preLastPath = breadcrumbs[breadcrumbs.length - 2];
    const lastPath = breadcrumbs[breadcrumbs.length - 1];
    const title = lastPath?.mobileTitle || preLastPath?.title;

    if (preLastPath) {
      return (
        <>
          {lastPath?.isMobileMode && <Box height="48px" />}
          <Box
            direction="row"
            background="canvas"
            height={lastPath?.isMobileMode ? '72px' : undefined}
            pad={{}}
            style={
              lastPath?.isMobileMode
                ? {
                    position: 'fixed',
                    left: 0,
                    right: 0,
                    top: 0,
                    zIndex: 99,
                  }
                : {}
            }
          >
            <RawButton onClick={() => handleRedirect(`${preLastPath.value}`)}>
              <Box direction="row" height="40px" align="center">
                {lastPath.isMobileMode ? (
                  <Box align="center" justify="center" width="48px" height="48px">
                    <NavigationLeftIcon color="textAccent" size="18px" />
                  </Box>
                ) : (
                  <NavigationLeftIcon color="textAccent" size="18px" />
                )}

                {lastPath?.isMobileSubtitleButton ? (
                  <Box direction="column" margin={{ left: 's' }}>
                    <Text weight={700} truncate title={lastPath.title} size="large" color="textAccent">
                      {lastPath.title}
                    </Text>
                    <Text weight={500} truncate title={preLastPath.title} size="small" color="textSecondary">
                      {preLastPath.title}
                    </Text>
                  </Box>
                ) : (
                  <Text weight={700} size="medium" color="textAccent" margin={{ left: 's' }}>
                    {title}
                  </Text>
                )}
              </Box>
            </RawButton>
          </Box>
        </>
      );
    }
    return null;
  }
  return (
    <Box direction="row">
      {backBtn ? (
        <BackButtonWrap
          data-testid="Breadcrumbs-BackButton-testid"
          onClick={() => backButtonHandler()}
          direction="row"
          align="center"
          margin={{ right: 'spacing24' }}
        >
          <NavigationLeftIcon color="textAccent" size="18px" />
          <Text margin={{ left: 'spacing8' }} color="textAccent" weight="bold">
            {backBtnTitle || 'Back'}
          </Text>
        </BackButtonWrap>
      ) : null}
      <Content
        data-testid="Breadcrumbs-content-testid"
        direction="row"
        align="center"
        pad={{ horizontal: 'spacing4', vertical: 'spacing2' }}
      >
        <SwitchImage src={faviconUrl || minimizedLogoUrl} width={ICON_SIZE} height={ICON_SIZE} alt="logo">
          {whiteLabelingConfig?.minifyLogo ? (
            <Image
              className="image"
              src={whiteLabelingConfig?.minifyLogo}
              alt="logo"
              height={ICON_SIZE}
              width={ICON_SIZE}
            />
          ) : (
            <ElevateLogoIcon />
          )}
        </SwitchImage>
        {breadcrumbs.map((item, index) => (
          <Box direction="row" justify="between" align="center" key={item.key}>
            {index !== 0 && <Delimiter key={`delimeter-${item.key}`}>/</Delimiter>}
            <Tip
              plain
              content={
                item.additionalValue ? (
                  <Box elevation="default" background="background3" pad="small">
                    <Text color="textOnColor">{item.additionalValue as unknown as string}</Text>
                  </Box>
                ) : null
              }
            >
              <Breadcrumb
                data-testid={`Breadcrumb-${item.title}`}
                onClick={() => handleRedirect(`${item.value}`)}
                key={item.key}
                disabled={lastIndex === index}
                grey={lastIndex === index}
              >
                {t(item.title || '')}
              </Breadcrumb>
            </Tip>
          </Box>
        ))}
      </Content>
    </Box>
  );
};
